import React, { useState, useEffect, createContext, useContext } from "react";
import { SchemaField } from "@/types/schemaBuilder";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Loader2, GripVertical, Eye, Edit } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface FormPreviewProps {
  schema: Record<string, SchemaField>;
  initialData?: Record<string, any>;
  onSave?: (values: Record<string, any>) => Promise<void>;
  title?: string;
  description?: string;
}

interface SortableArrayItemProps {
  id: string;
  field: SchemaField;
  index: number;
  isViewMode?: boolean;
  onToggleViewMode?: () => void;
  children: React.ReactNode;
}

interface FormPreviewContextType {
  itemSequences: Record<string, number[]>;
}

const FormPreviewContext = createContext<FormPreviewContextType>({
  itemSequences: {},
});

const SortableArrayItem: React.FC<SortableArrayItemProps> = ({
  id,
  field,
  index,
  isViewMode,
  onToggleViewMode,
  children,
}) => {
  // Extract the fieldName from the id (format: fieldName-index)
  const fieldName = id.split("-")[0];

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    data: { index }, // Store the index in the sortable item's data
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.8 : 1,
    position: isDragging ? "relative" : undefined,
  } as React.CSSProperties;

  // Access itemSequences from FormPreview component context
  const { itemSequences } = React.useContext(FormPreviewContext);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border-l-2 border-border pl-4 py-2 mb-2 ${isDragging ? "bg-muted/20" : ""}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          {field.items?.sortable && (
            <button
              type="button"
              className="p-1 hover:bg-muted rounded cursor-grab active:cursor-grabbing"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4 text-ink-faint" />
            </button>
          )}
          <h4 className="text-sm font-medium">
            Item {itemSequences[fieldName]?.[index] || index + 1}
          </h4>
        </div>
        {field.items?.viewable && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              onToggleViewMode?.();
            }}
            className="ml-2"
          >
            {isViewMode ? (
              <Edit className="h-4 w-4 mr-1" />
            ) : (
              <Eye className="h-4 w-4 mr-1" />
            )}
            {isViewMode ? "Edit" : "View"}
          </Button>
        )}
      </div>
      {children}
    </div>
  );
};

const FormPreview: React.FC<FormPreviewProps> = ({
  schema,
  initialData,
  onSave,
  title = "Form Preview",
  description,
}) => {
  console.log("FormPreview received schema:", schema);
  console.log("Schema values:", Object.values(schema));
  console.log("Initial data:", initialData);

  const [formValues, setFormValues] = useState<Record<string, any>>(
    initialData || {},
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewModeItems, setViewModeItems] = useState<Record<string, boolean>>(
    {},
  );
  const [itemSequences, setItemSequences] = useState<Record<string, number[]>>(
    {},
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    if (initialData) {
      setFormValues(initialData);

      // Initialize sequence numbers for array fields
      const sequences: Record<string, number[]> = {};
      Object.entries(initialData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          sequences[key] = value.map((_, i) => i + 1);
        }
      });
      setItemSequences(sequences);
    }
  }, [initialData]);

  // Debug effect to log form values changes
  useEffect(() => {
    console.log("Form values updated:", formValues);
  }, [formValues]);

  // Helper function to get a nested value using a path string
  const getNestedValue = (obj: any, path: string) => {
    return path
      .split(".")
      .reduce(
        (acc, part) => (acc && acc[part] !== undefined ? acc[part] : ""),
        obj,
      );
  };

  // Helper function to set a nested value using a path string
  const setNestedValue = (obj: any, path: string, value: any) => {
    const parts = path.split(".");
    const lastPart = parts.pop()!;
    const target = parts.reduce((acc, part) => {
      if (!(part in acc)) {
        // If the part is a number, create an array
        acc[part] = /^\d+$/.test(parts[parts.indexOf(part) + 1] || "")
          ? []
          : {};
      }
      return acc[part];
    }, obj);
    target[lastPart] = value;
    return { ...obj };
  };

  const handleInputChange = (path: string, value: any) => {
    setFormValues((prev) => setNestedValue(prev, path, value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Form submitted with values:", formValues);

    if (onSave) {
      setIsSubmitting(true);
      try {
        await onSave(formValues);
      } catch (error) {
        console.error("Error saving form values:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent, fieldName: string) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      // Extract indices from the IDs
      const activeId = active.id.toString();
      const overId = over.id.toString();

      const oldIndex = parseInt(activeId.split("-").pop() || "0");
      const newIndex = parseInt(overId.split("-").pop() || "0");

      console.log(
        `Drag end detected: moving item from ${oldIndex} to ${newIndex} in field ${fieldName}`,
        { activeId, overId },
      );

      setFormValues((prev) => {
        // Make a deep copy of the previous state
        const prevCopy = JSON.parse(JSON.stringify(prev));
        const currentItems = Array.isArray(prevCopy[fieldName])
          ? prevCopy[fieldName]
          : [];

        // Use arrayMove to reorder the items
        const reorderedItems = arrayMove(currentItems, oldIndex, newIndex);

        console.log("Reordering:", {
          oldIndex,
          newIndex,
          currentItems: JSON.stringify(currentItems),
          reorderedItems: JSON.stringify(reorderedItems),
          fieldName,
        });

        // Create a new state object with the reordered items
        const result = {
          ...prevCopy,
          [fieldName]: reorderedItems,
        };

        return result;
      });

      // Also update the sequence numbers to maintain their association with items
      setItemSequences((prev) => {
        const prevSequences = [...(prev[fieldName] || [])];
        if (prevSequences.length > 0) {
          return {
            ...prev,
            [fieldName]: arrayMove(prevSequences, oldIndex, newIndex),
          };
        }
        return prev;
      });
    }
  };

  const toggleViewMode = (itemId: string) => {
    setViewModeItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const renderArrayItems = (field: SchemaField, fieldName: string) => {
    const items = formValues[fieldName] || [];
    // Generate stable IDs that include both fieldName and index
    const itemIds = items.map(
      (_: any, index: number) => `${fieldName}-${index}`,
    );

    console.log(`Rendering array items for ${fieldName}:`, {
      itemCount: items.length,
      itemIds,
      sortable: field.items?.sortable,
      formValues: JSON.stringify(formValues[fieldName]),
    });

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(event) => handleDragEnd(event, fieldName)}
      >
        {field.items?.sortable ? (
          <SortableContext
            items={itemIds}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {items.map((item: any, index: number) => (
                <SortableArrayItem
                  key={`${fieldName}-${index}`}
                  id={`${fieldName}-${index}`}
                  field={field}
                  index={index}
                  isViewMode={viewModeItems[`${fieldName}-${index}`]}
                  onToggleViewMode={() =>
                    toggleViewMode(`${fieldName}-${index}`)
                  }
                >
                  {renderArrayItemContent(field, item, index, fieldName)}
                </SortableArrayItem>
              ))}
            </div>
          </SortableContext>
        ) : (
          items.map((item: any, index: number) => (
            <div
              key={`${fieldName}-${index}`}
              className="border-l-2 border-border pl-4 py-2 mb-2"
            >
              {renderArrayItemContent(field, item, index, fieldName)}
            </div>
          ))
        )}
      </DndContext>
    );
  };

  const renderArrayItemContent = (
    field: SchemaField,
    item: any,
    index: number,
    fieldName: string,
  ) => {
    const isViewMode = viewModeItems[`${fieldName}-${index}`];

    if (field.items?.type === "object" && field.items.properties) {
      if (isViewMode && field.items.viewable) {
        // Render read-only view
        return (
          <div className="space-y-2 p-2 bg-muted/10 rounded-md">
            {Object.entries(field.items.properties).map(
              ([propName, propField]) => (
                <div key={propName} className="flex flex-col">
                  <span className="text-sm font-medium">
                    {propField.title || propName}
                  </span>
                  <span className="text-sm">{item[propName]}</span>
                </div>
              ),
            )}
          </div>
        );
      } else {
        // Render editable form
        return (
          <div className="space-y-4 p-2 bg-muted/20 rounded-md">
            {Object.entries(field.items.properties).map(
              ([propName, propField]) =>
                renderField(propField, `${fieldName}.${index}.${propName}`),
            )}
          </div>
        );
      }
    } else {
      return (
        <div className="mb-4">
          <Label htmlFor={`${fieldName}.${index}`} className="mb-1 block">
            Value
          </Label>
          <Input
            id={`${fieldName}.${index}`}
            value={item || ""}
            onChange={(e) => {
              const newItems = [...(formValues[fieldName] || [])];
              newItems[index] = e.target.value;
              handleInputChange(fieldName, newItems);
            }}
            placeholder="Enter value"
            className="w-full"
          />
        </div>
      );
    }
  };

  const renderField = (field: SchemaField, path: string = field.name) => {
    const value = getNestedValue(formValues, path);

    switch (field.type) {
      case "string":
        return (
          <div className="mb-4" key={path}>
            <Label htmlFor={path} className="mb-1 block">
              {field.title || field.name}
              {field.required && <span className="text-danger ml-1">*</span>}
            </Label>
            {field.nodes && (
              <p className="text-xs text-primary italic mb-1">{field.nodes}</p>
            )}
            {field.description && (
              <p className="text-sm text-muted-foreground mb-2">
                {field.description}
              </p>
            )}
            <Input
              id={path}
              placeholder={`Enter ${field.title || field.name}`}
              className="w-full"
              value={value || ""}
              onChange={(e) => handleInputChange(path, e.target.value)}
            />
          </div>
        );

      case "number":
        return (
          <div className="mb-4" key={path}>
            <Label htmlFor={path} className="mb-1 block">
              {field.title || field.name}
              {field.required && <span className="text-danger ml-1">*</span>}
            </Label>
            {field.nodes && (
              <p className="text-xs text-primary italic mb-1">{field.nodes}</p>
            )}
            {field.description && (
              <p className="text-sm text-muted-foreground mb-2">
                {field.description}
              </p>
            )}
            <Input
              id={path}
              type="number"
              placeholder={`Enter ${field.title || field.name}`}
              className="w-full"
              value={value || ""}
              onChange={(e) =>
                handleInputChange(
                  path,
                  e.target.value ? Number(e.target.value) : "",
                )
              }
            />
          </div>
        );

      case "boolean":
        return (
          <div className="mb-4 flex items-start space-x-2" key={path}>
            <Switch
              id={path}
              checked={value || false}
              onCheckedChange={(checked) => handleInputChange(path, checked)}
            />
            <div>
              <Label htmlFor={path} className="mb-1 block">
                {field.title || field.name}
                {field.required && <span className="text-danger ml-1">*</span>}
              </Label>
              {field.nodes && (
                <p className="text-xs text-primary italic mb-1">
                  {field.nodes}
                </p>
              )}
              {field.description && (
                <p className="text-sm text-muted-foreground">
                  {field.description}
                </p>
              )}
            </div>
          </div>
        );

      case "object":
        return (
          <div className="mb-6 border p-4 rounded-md" key={path}>
            <h3 className="text-md font-medium mb-3">
              {field.title || field.name}
            </h3>
            {field.nodes && (
              <p className="text-xs text-primary italic mb-1">{field.nodes}</p>
            )}
            {field.description && (
              <p className="text-sm text-muted-foreground mb-3">
                {field.description}
              </p>
            )}
            {field.properties &&
              Object.entries(field.properties).map(([propName, propField]) =>
                renderField(propField, `${path}.${propName}`),
              )}
          </div>
        );

      case "array":
        return (
          <div className="mb-6 border p-4 rounded-md" key={path}>
            <h3 className="text-md font-medium mb-2">
              {field.title || field.name}
            </h3>
            {field.nodes && (
              <p className="text-xs text-primary italic mb-1">{field.nodes}</p>
            )}
            {field.description && (
              <p className="text-sm text-muted-foreground mb-3">
                {field.description}
              </p>
            )}

            <div className="mb-3">{renderArrayItems(field, path)}</div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => handleAddItem(path, field)}
            >
              + Add Item
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  const handleAddItem = (fieldName: string, field: SchemaField) => {
    setFormValues((prev) => {
      // Make a deep copy of the previous state
      const prevCopy = JSON.parse(JSON.stringify(prev));
      const currentItems = prevCopy[fieldName] || [];
      let newItem: any;

      if (field.items?.type === "object" && field.items.properties) {
        // Create a new object with default values for each property
        newItem = Object.entries(field.items.properties).reduce(
          (acc, [propName, propField]) => {
            acc[propName] =
              propField.default !== undefined ? propField.default : "";
            return acc;
          },
          {} as Record<string, any>,
        );
      } else {
        // For non-object items, just add an empty value
        newItem = "";
      }

      // Create a new array with the new item
      const updatedItems = [...currentItems, newItem];
      console.log(`Adding new item to ${fieldName}:`, {
        currentItems: JSON.stringify(currentItems),
        updatedItems: JSON.stringify(updatedItems),
      });

      return {
        ...prevCopy,
        [fieldName]: updatedItems,
      };
    });

    // Add a new sequence number for the new item
    setItemSequences((prev) => {
      const currentSequences = prev[fieldName] || [];
      const nextSequence =
        currentSequences.length > 0 ? Math.max(...currentSequences) + 1 : 1;

      return {
        ...prev,
        [fieldName]: [...currentSequences, nextSequence],
      };
    });
  };

  return (
    <FormPreviewContext.Provider value={{ itemSequences }}>
      <Card className="h-full flex flex-col">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent className="flex-1 overflow-auto">
          <ScrollArea className="h-full">
            <form onSubmit={handleSubmit}>
              {Object.entries(schema).map(([fieldName, field]) => (
                <div key={fieldName}>{renderField(field)}</div>
              ))}

              {Object.keys(schema).length > 0 && (
                <>
                  <div className="flex justify-end mt-6">
                    <Button
                      type="button"
                      variant="outline"
                      className="mr-2"
                      onClick={() => setFormValues(initialData || {})}
                      disabled={isSubmitting}
                    >
                      Reset
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </div>

                  {/* Notes section */}
                  {Object.values(schema).some((field) => field.notes) && (
                    <div className="mt-8 pt-4 border-t border-border">
                      <h3 className="text-sm font-medium mb-2">Notes:</h3>
                      {Object.values(schema).map((field, index) =>
                        field.notes ? (
                          <div key={`note-${index}`} className="mb-2 text-sm">
                            <span className="font-medium">
                              {field.title || field.name}:
                            </span>{" "}
                            {field.notes}
                          </div>
                        ) : null,
                      )}
                    </div>
                  )}
                </>
              )}
            </form>
          </ScrollArea>
        </CardContent>
      </Card>
    </FormPreviewContext.Provider>
  );
};

export default FormPreview;
